import { Flex, Tag } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, orDash } from '../../lib/format';
import {
  CUSTOMER_HISTORY,
  type CustomerDelivery,
  type CustomerRegistrationCase,
  type CustomerUnit,
} from './api';
import { HistoryCard } from './HistoryCard';
import { HistorySection } from './HistorySection';

/** The customer's motorcycles, their AP / Puspakom / JPJ cases and their handovers. */
export function RegistrationTab({ customerUid }: { customerUid: string }): ReactNode {
  const { t } = useTranslation();
  const unitStatus = (s: string): string => t(`vehicles.status.${s}`, { defaultValue: s });
  const caseStatus = (s: string): string => t(`customers.history.caseStatus.${s}`, { defaultValue: s });
  const stage = (s: string | null): string =>
    s === null ? '—' : t(`customers.history.regStage.${s}`, { defaultValue: s });
  const deliveryStatus = (s: string): string => t(`delivery.status.${s}`, { defaultValue: s });

  return (
    <Flex vertical gap={24}>
      <HistorySection<CustomerUnit>
        list="units"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY.units(customerUid, cursor)}
        title={t('customers.history.units.title')}
        empty={t('customers.history.units.empty')}
        forbidden={t('customers.history.units.forbidden')}
        href={(row) => `/units/${row.uid}`}
        columns={[
          { title: t('customers.history.col.chassisNo'), dataIndex: 'chassisNo', width: 200 },
          { title: t('customers.history.col.model'), dataIndex: 'model', render: (v: string | null) => orDash(v) },
          { title: t('customers.history.col.colour'), dataIndex: 'colour', render: (v: string | null) => orDash(v) },
          { title: t('customers.history.col.plateNo'), dataIndex: 'plateNo', render: (v: string | null) => orDash(v) },
          {
            title: t('customers.history.col.status'),
            dataIndex: 'stockStatus',
            render: (v: string) => <Tag>{unitStatus(v)}</Tag>,
          },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard number={row.chassisNo} status={unitStatus(row.stockStatus)} lines={[row.model, row.plateNo]} />
        )}
      />

      <HistorySection<CustomerRegistrationCase>
        list="registration-cases"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY['registration-cases'](customerUid, cursor)}
        title={t('customers.history.registration.title')}
        empty={t('customers.history.registration.empty')}
        forbidden={t('customers.history.registration.forbidden')}
        href={(row) => `/registration/${row.uid}`}
        columns={[
          { title: t('customers.history.col.caseNo'), dataIndex: 'caseNo', width: 180 },
          {
            title: t('customers.history.col.status'),
            dataIndex: 'status',
            render: (v: string) => <Tag>{caseStatus(v)}</Tag>,
          },
          { title: t('customers.history.col.currentStage'), dataIndex: 'currentStage', render: (v: string | null) => stage(v) },
          { title: t('customers.history.col.chassisNo'), dataIndex: 'chassisNo' },
          { title: t('customers.history.col.plateNo'), dataIndex: 'plateNo', render: (v: string | null) => orDash(v) },
          { title: t('customers.history.col.opened'), dataIndex: 'openedAt', width: 130, render: (v: string) => formatDate(v) },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={row.caseNo}
            status={caseStatus(row.status)}
            lines={[row.chassisNo, row.currentStage === null ? null : stage(row.currentStage)]}
          />
        )}
      />

      <HistorySection<CustomerDelivery>
        list="deliveries"
        customerUid={customerUid}
        fetchPage={(cursor) => CUSTOMER_HISTORY.deliveries(customerUid, cursor)}
        title={t('customers.history.deliveries.title')}
        empty={t('customers.history.deliveries.empty')}
        forbidden={t('customers.history.deliveries.forbidden')}
        href={(row) => `/deliveries/${row.uid}`}
        columns={[
          { title: t('customers.history.col.doNo'), dataIndex: 'doNo', width: 180 },
          {
            title: t('customers.history.col.status'),
            dataIndex: 'status',
            render: (v: string) => <Tag>{deliveryStatus(v)}</Tag>,
          },
          { title: t('customers.history.col.chassisNo'), dataIndex: 'chassisNo' },
          {
            title: t('customers.history.col.scheduled'),
            dataIndex: 'scheduledAt',
            width: 130,
            render: (v: string | null) => formatDate(v),
          },
          {
            title: t('customers.history.col.delivered'),
            dataIndex: 'deliveredAt',
            width: 130,
            render: (v: string | null) => formatDate(v),
          },
        ]}
        renderMobileRow={(row) => (
          <HistoryCard
            number={row.doNo}
            status={deliveryStatus(row.status)}
            lines={[row.chassisNo, row.deliveredAt === null ? null : formatDate(row.deliveredAt)]}
          />
        )}
      />
    </Flex>
  );
}
